import { useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Info } from "lucide-react"
import { cn } from "@/lib/utils"
import { glossary } from "@/lib/glossary"

export interface InfoPopoverProps {
  /** Glossary key (see @/lib/glossary). Unknown terms render nothing. */
  term: string
  className?: string
}

/**
 * Small "i" affordance that reveals a glossary definition on hover / focus / tap.
 * The tooltip is rendered through a portal with `position: fixed` so it escapes
 * any `overflow-hidden` ancestor (e.g. the hero card) instead of being clipped.
 * Presentational + SSR-safe (closed on first render; portal only on the client).
 */
export function InfoPopover({ term, className }: InfoPopoverProps) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const entry = glossary[term]
  if (!entry) return null

  const show = () => {
    const el = btnRef.current
    if (el) {
      const r = el.getBoundingClientRect()
      setPos({ top: r.bottom + 6, left: r.left + r.width / 2 })
    }
    setOpen(true)
  }
  const hide = () => setOpen(false)

  return (
    <span className={cn("relative inline-flex", className)}>
      <button
        ref={btnRef}
        type="button"
        aria-label={`What is ${entry.title}?`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => {
          e.preventDefault()
          open ? hide() : show()
        }}
        className="inline-flex text-stone-300 transition-colors hover:text-stone-500 dark:text-stone-600 dark:hover:text-stone-400"
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {open && pos && typeof document !== "undefined"
        ? createPortal(
            <span
              role="tooltip"
              style={{ position: "fixed", top: pos.top, left: pos.left, transform: "translateX(-50%)" }}
              className="pointer-events-none z-[100] w-60 rounded-xl border border-stone-200 bg-white p-3 text-left text-xs font-normal normal-case leading-relaxed tracking-normal text-stone-600 shadow-lg dark:border-stone-700 dark:bg-stone-900 dark:text-stone-300"
            >
              <span className="mb-0.5 block font-semibold text-stone-900 dark:text-stone-100">{entry.title}</span>
              {entry.body}
            </span>,
            document.body,
          )
        : null}
    </span>
  )
}

export default InfoPopover
